import {Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faChevronDown, faChevronRight} from '@fortawesome/free-solid-svg-icons';
import {BicTextareaComponent} from '@buildinginformationcloud/bic-ui-components';
import {createMemo} from '../../../../memo';
import {JsonSchema, toVariableChip, VariableChip} from '../schema-form.types';

/** Platzhalter eines Quellschritts; `origin` leer für Platzhalter ohne bekannte Herkunft. */
export interface VariableGroup {
  origin: string;
  chips: VariableChip[];
}

/**
 * Gruppiert Platzhalter nach ihrer Herkunft, in der Reihenfolge des ersten Auftretens (die Liste
 * kommt bereits in Ablaufreihenfolge). Platzhalter ohne Herkunft bilden eine Gruppe ohne Überschrift.
 */
export function groupVariables(chips: VariableChip[]): VariableGroup[] {
  const groups: VariableGroup[] = [];
  const byOrigin = new Map<string, VariableGroup>();
  for (const chip of chips) {
    const origin = chip.origin ?? '';
    let group = byOrigin.get(origin);
    if (!group) {
      group = {origin, chips: []};
      byOrigin.set(origin, group);
      groups.push(group);
    }
    group.chips.push(chip);
  }
  return groups;
}

/**
 * x-widget:textarea → multi-line text.
 *
 * Nutzt `bic-textarea` aus dem Design-System, damit Prompt-Felder im Knoten-Panel genauso
 * aussehen wie Systemprompt/Prüfprompt im KI-Editor (vorher: handgerolltes `<textarea>` mit
 * eigenen Tailwind-Klassen, das bei jeder Änderung am Design-System auseinanderlief).
 *
 * When the schema carries `x-variables` (resolved from `x-variableSource` by the node-config
 * panel), a list of chips (grouped by source step, collapsible) lets the user insert `{Variablenname}` at the current cursor position
 * instead of typing it by hand — dieselbe Chip-Optik wie im KI-Editor.
 *
 * Trägt ein Platzhalter mögliche Werte ({@link VariableChip.options}, z.B. ein Auswahl-Ausgabe-
 * parameter der KI-Anweisung), stehen sie grau daneben. Ohne sie wüsste der Nutzer
 * nur, DASS ein Wert verfügbar ist, aber nicht, was er annehmen kann.
 */
@Component({
  selector: 'sf-textarea',
  standalone: true,
  imports: [BicTextareaComponent, FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-2 w-full">
      <bic-textarea
        #bicTextarea
        [rows]="rows"
        [value]="value ?? ''"
        [placeholder]="placeholder"
        [resizable]="true"
        [disabled]="disabled"
        (input)="valueChange.emit($any($event.target).value)"></bic-textarea>
      @if (variables.length) {
        <!-- Platzhalter als Liste untereinander, nach Quellschritt gruppiert. Ein Klick fügt ein; die
             möglichen Werte stehen grau daneben und brechen um. Vorher eine Chip-Reihe mit Werte-
             Badges, die bei schmalem Panel rechts abgeschnitten wurde. -->
        <div class="flex flex-col gap-1.5">
          <button type="button" (click)="variablesOpen = !variablesOpen"
                  [attr.aria-expanded]="variablesOpen"
                  class="self-start inline-flex items-center gap-1.5 text-xs font-medium text-gray-600
                         hover:text-gray-900 transition-colors">
            <fa-icon [icon]="variablesOpen ? faChevronDown : faChevronRight" class="text-[10px] w-2.5"></fa-icon>
            Platzhalter einfügen ({{ variables.length }})
          </button>
          @if (variablesOpen) {
            <div class="flex flex-col gap-2 pl-4">
              @for (group of variableGroups; track group.origin) {
                <div class="flex flex-col gap-1">
                  @if (group.origin) {
                    <span class="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{{ group.origin }}</span>
                  }
                  @for (v of group.chips; track v.name) {
                    <div class="flex items-start gap-2">
                      <button type="button" (click)="insertVariable(v.name)"
                              [title]="chipTitle(v)"
                              class="shrink-0 max-w-[60%] px-2 py-0.5 rounded-md bg-gray-200 text-gray-700 text-xs
                                     font-medium text-left break-words hover:bg-gray-300 transition-colors">
                        {{ '{' + v.name + '}' }}
                      </button>
                      @if (v.options?.length) {
                        <span class="flex-1 min-w-0 pt-0.5 text-[11px] leading-4 text-gray-400 break-words">
                          {{ v.options!.join(' · ') }}
                        </span>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class SfTextareaComponent {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  /** Read-only, z.B. weil die Zeile vom Modul verwaltet wird (siehe `x-managed-items`). */
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<any>();

  @ViewChild('bicTextarea') bicTextarea?: BicTextareaComponent;

  /** Platzhalter-Liste aufgeklappt; nur für diese Ansicht, nicht gespeichert. */
  variablesOpen = true;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronRight = faChevronRight;
  private readonly memo = createMemo();

  /**
   * Das native `<textarea>` innerhalb von `bic-textarea` — nur dort stehen selectionStart/-End,
   * die das Einfügen an der Cursorposition braucht. `bic-textarea` gibt es als `textareaEl` heraus.
   */
  private get nativeTextarea(): HTMLTextAreaElement | null {
    return this.bicTextarea?.textareaEl?.nativeElement ?? null;
  }

  get placeholder(): string {
    return this.schema?.['x-placeholder'] ?? 'Text eingeben…';
  }

  /** Zeilenhöhe aus dem Schema — kurze Felder (z.B. Beschreibung einer Karte) brauchen weniger. */
  get rows(): number {
    const rows = Number(this.schema?.['x-rows']);
    return Number.isFinite(rows) && rows > 0 ? rows : 4;
  }

  /**
   * Einfügbare Platzhalter. `x-variables` trägt entweder Chip-Objekte (mit möglichen Werten) oder
   * reine Strings — Letzteres bleibt für Aufrufer erhalten, die keine Optionen kennen.
   */
  get variables(): VariableChip[] {
    const raw = this.schema?.['x-variables'];
    return Array.isArray(raw) ? raw.map(toVariableChip).filter(chip => !!chip?.name) : [];
  }

  /** Platzhalter nach Quellschritt gruppiert – gecacht, solange das Schema dasselbe ist. */
  get variableGroups(): VariableGroup[] {
    return this.memo('variableGroups', [this.schema?.['x-variables']], () => groupVariables(this.variables));
  }

  /** Tooltip eines Chips: Einfüge-Hinweis, Herkunft und – falls Auswahl – die möglichen Werte. */
  chipTitle(chip: VariableChip): string {
    const parts = [`{${chip.name}} einfügen`];
    if (chip.origin) parts.push(`aus ${chip.origin}`);
    if (chip.options?.length) parts.push(`Mögliche Werte: ${chip.options.join(', ')}`);
    return parts.join(' · ');
  }

  /** Inserts `{name}` at the current cursor position (replacing any selection). */
  insertVariable(name: string): void {
    const token = `{${name}}`;
    const current = (this.value ?? '').toString();
    const el = this.nativeTextarea;

    if (!el) {
      this.valueChange.emit(`${current}${token}`);
      return;
    }

    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    this.valueChange.emit(next);

    const caret = start + token.length;
    queueMicrotask(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }
}
